import { ChangeDetectionStrategy, Component, inject, Signal, ViewEncapsulation } from '@angular/core';
import { IQuizQuestion } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz';
import { FiQuizEditorStoreService } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-edit/fi-quiz-editor-store.service';
import { FiQuizHelperService } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-helper.service';
import { ObjectTypes } from '@fi-shared/types';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { FiQuizActionsBuilderService } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-actions-builder.service';
import { IQuizQuestionPresetOption } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz';
import { FiQuizQuestionType } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-enums';
import { FiToggleSwitchSliderColorClass } from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';
import { ControlValueAccessorValidatorBase } from '@fi-app/shared/control-value-accessor-validator-base';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, ValidationErrors, AbstractControl } from '@angular/forms';
import { Animations } from '@fi-app/shared/utils/animations';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { IContentLanguageLabelModel } from '@fi-app/shared/fi-content-language-shared/fi-content-language-shared';

@Component({
    selector: 'fi-quiz-question-card',
    templateUrl: './quiz-question-card.component.html',
    styleUrls: ['./quiz-question-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-quiz-question-card' },
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiQuizQuestionCardComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiQuizQuestionCardComponent,
        },
    ],
    animations: [Animations.fadeWidth()],
})
export class FiQuizQuestionCardComponent extends ControlValueAccessorValidatorBase<IQuizQuestion> {
    private fiQuizEditorStoreService = inject(FiQuizEditorStoreService);
    private fiQuizHelperService = inject(FiQuizHelperService);
    private fiQuizActionsBuilderService = inject(FiQuizActionsBuilderService);

    protected selectedLanguageId: Signal<number> = this.fiQuizEditorStoreService.selectedLanguageId;

    protected readonly actions: IItemsListAction2<IQuizQuestion>[] =
        this.fiQuizActionsBuilderService.getQuizQuestionActions();

    protected getOptionInstanceFn: () => IQuizQuestionPresetOption = () => {
        return this.fiQuizHelperService.getNewQuizQuestionPresetOptionInstance();
    };

    protected isOptionNameRequiredFn = (optionName: IContentLanguageLabelModel, option: IQuizQuestionPresetOption) => {
        const hasAtLeastOneOptionName = option.Names.some(
            (name: IContentLanguageLabelModel) => !!name.Value?.trim().length,
        );

        return !hasAtLeastOneOptionName;
    };

    protected isDeleteOptionButtonDisabled(): boolean {
        return this.ngModel.PresetOptions.length <= 2;
    }

    protected onOptionCorrectStatusChange(optionIndex: number, isCorrect: boolean): void {
        if (!this.isMultipleChoiceQuestion()) {
            this.ngModel.PresetOptions = this.ngModel.PresetOptions.map(
                (option: IQuizQuestionPresetOption, index: number) => {
                    return { ...option, IsCorrect: index === optionIndex ? isCorrect : false };
                },
            );
        }
        this.doPropagateChanges();
    }

    protected isMultipleChoiceQuestion(): boolean {
        return this.ngModel?.Type === FiQuizQuestionType.MultipleAnswers;
    }

    public doValidate(control: AbstractControl): ValidationErrors | null {
        return {
            'missing-correct-option': !this.ngModel.PresetOptions?.some(
                (option: IQuizQuestionPresetOption) => !!option.IsCorrect,
            ),
        };
    }

    protected isErrorShown(): boolean {
        return (
            this.fiQuizEditorStoreService.formFailedValidation() &&
            this.controlComponent?.hasError('missing-correct-option')
        );
    }

    public getControl(): AbstractControl | undefined {
        return this.controlComponent;
    }

    protected readonly ObjectTypes = ObjectTypes;
    protected readonly FiToggleSwitchSliderColorClass = FiToggleSwitchSliderColorClass;
    protected readonly FiInfoIconMode = FiInfoIconMode;
}
